import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import useNotification from "../components/notifications/useNotifications";
import useDepollutionSession from "../data/useDepollutionSession";

export default function useDepollutionTimers(depollutionId?: string) {
  const { addNotification } = useNotification();
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const { sessions: depollutionSessions, startSession, pauseSession, finishSession } = useDepollutionSession(depollutionId);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const parseApiUtcDate = (value: string) => DateTime.fromISO(value, { zone: "utc" });

  const sessionRows = depollutionSessions.data?.data ?? [];

  const totalElapsedSeconds = useMemo(() => {
    return sessionRows.reduce((total, row) => {
      if (!row.dismantlingStart) return total;

      const start = parseApiUtcDate(row.dismantlingStart);
      const end = row.dismantlingEnd
        ? parseApiUtcDate(row.dismantlingEnd)
        : DateTime.utc();
      const diff = Math.floor(end.diff(start, "seconds").seconds);

      return total + (diff > 0 ? diff : 0);
    }, 0);
  }, [sessionRows, nowMs]);

  const firstStartedSession = useMemo(() => {
    return [...sessionRows]
      .filter((x) => !!x.dismantlingStart)
      .sort((a, b) => parseApiUtcDate(a.dismantlingStart!).toMillis() - parseApiUtcDate(b.dismantlingStart!).toMillis())[0];
  }, [sessionRows]);

  const lastSessionActivity = useMemo(() => {
    return [...sessionRows].sort((a, b) => {
      const aActivity = a.dismantlingEnd ?? a.dismantlingStart ?? a.created;
      const bActivity = b.dismantlingEnd ?? b.dismantlingStart ?? b.created;
      return parseApiUtcDate(bActivity).toMillis() - parseApiUtcDate(aActivity).toMillis();
    })[0];
  }, [sessionRows]);

  const hasActiveSession = useMemo(() => sessionRows.some((x) => !x.dismantlingEnd), [sessionRows]);
  const isSessionActionLoading = startSession.isLoading || pauseSession.isLoading || finishSession.isLoading;
  const isSessionsLoading = depollutionSessions.isLoading;

  const handleStart = () => {
    if (!depollutionId) return;

    startSession.mutate(
      { depollutionId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Depollution timer started",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to start depollution timer",
          });
        }
      }
    );
  };

  const handlePause = (userId: string, pauseReason = "Paused") => {
    if (!depollutionId) return;

    pauseSession.mutate(
      { depollutionId, userId, pauseReason },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Depollution timer paused",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to pause depollution timer",
          });
        }
      }
    );
  };

  const handleFinish = (userId: string) => {
    if (!depollutionId) return;

    finishSession.mutate(
      { depollutionId, userId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Depollution timer finished",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to finish depollution timer",
          });
        }
      }
    );
  };

  return {
    sessionRows,
    totalElapsedSeconds,
    firstStartedAt: firstStartedSession?.dismantlingStart ?? null,
    lastActivityAt: lastSessionActivity
      ? (lastSessionActivity.dismantlingEnd ?? lastSessionActivity.dismantlingStart ?? lastSessionActivity.created)
      : null,
    hasActiveSession,
    isSessionsLoading,
    isSessionActionLoading,
    handleStart,
    handlePause,
    handleFinish,
  };
}
